import { useEffect, useRef, useState } from 'react'
import { Eraser, Undo2 } from 'lucide-react'
import { boardKey, parseStrokes, strokePath, thin, type Stroke } from '@/lib/panel'
import { Button } from './ui'

const W = 1000
const H = 800

/**
 * A plain whiteboard to sketch on beside a page: one pen, undo, clear. The
 * strokes stay on this device, per page (localStorage), and never leave it.
 */
export const Whiteboard = ({ pageId }: { pageId: string }) => {
  const [strokes, setStrokes] = useState<Stroke[]>(() => {
    try {
      return parseStrokes(localStorage.getItem(boardKey(pageId)))
    } catch {
      return []
    }
  })
  const [live, setLive] = useState<Stroke | null>(null)
  const svg = useRef<SVGSVGElement>(null)
  // the stroke under the finger, kept in a ref too so lifting it adds exactly one
  const current = useRef<Stroke | null>(null)

  useEffect(() => {
    try {
      if (strokes.length) localStorage.setItem(boardKey(pageId), JSON.stringify(strokes))
      else localStorage.removeItem(boardKey(pageId))
    } catch {
      /* storage full or private: the sketch lives while the tab does */
    }
  }, [strokes, pageId])

  const at = (e: React.PointerEvent): [number, number] => {
    const r = svg.current!.getBoundingClientRect()
    return [((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H]
  }
  const end = () => {
    const done = current.current
    current.current = null
    setLive(null)
    if (done && done.length) setStrokes((s) => [...s, thin(done)])
  }

  return (
    <div className="flex flex-col gap-2 h-full min-h-0" data-testid="whiteboard">
      <div className="flex items-center justify-between">
        <span className="label text-muted">Whiteboard</span>
        <span className="flex gap-1.5">
          <Button
            variant="chip"
            onClick={() => setStrokes((s) => s.slice(0, -1))}
            disabled={strokes.length === 0}
            aria-label="Undo the last stroke"
            data-testid="whiteboard-undo"
          >
            <Undo2 size={14} aria-hidden />
          </Button>
          <Button
            variant="chip"
            onClick={() => setStrokes([])}
            disabled={strokes.length === 0}
            aria-label="Clear the whiteboard"
            data-testid="whiteboard-clear"
          >
            <Eraser size={14} aria-hidden />
          </Button>
        </span>
      </div>
      <svg
        ref={svg}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full flex-1 min-h-0 rounded-[var(--radius-card-inner)] border border-line bg-ink touch-none cursor-crosshair"
        role="img"
        aria-label="Whiteboard: draw with a pointer or a finger"
        data-testid="whiteboard-surface"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId)
          current.current = [at(e)]
          setLive(current.current)
        }}
        onPointerMove={(e) => {
          if (!current.current) return
          current.current = [...current.current, at(e)]
          setLive(current.current)
        }}
        onPointerUp={end}
        onPointerCancel={end}
      >
        <g fill="none" stroke="var(--color-fg)" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
          {strokes.map((s, i) => (
            <path key={i} d={strokePath(s)} />
          ))}
          {live && <path d={strokePath(live)} />}
        </g>
      </svg>
    </div>
  )
}
